import { useSortable } from "@dnd-kit/sortable";
import React, { useMemo } from "react";
import _ from "lodash";
import cx from "classnames";

import { useNavigate } from "react-router-dom";
import { FileService } from "src/services/file.service";
import { CSS } from '@dnd-kit/utilities';
import { Assessment, Lesson } from "src/types/coaching/courses";

import styles from "./cards.module.scss";
import Thumbnail from "src/components/thumbnail";
import LinesEllipsis from "react-lines-ellipsis";
import { useSelector } from "react-redux";
import Text from "src/components/text";

export interface LessonCardProps {
  lesson: Lesson;
  index: number;
  lessonIndex: number;
  className?: string;
  active: boolean;
  readonly?: boolean;
  // eslint-disable-next-line no-unused-vars
  onClick: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  onRemove?: (index: number) => void;
}

export const LessonCard: React.FC<LessonCardProps> = ({ lesson, index, lessonIndex, className, active, readonly, onClick, onRemove }) => {
  const navigate = useNavigate();

  const asset = _.get(lesson, 'asset');
  const name = _.get(lesson, 'name') || "Untitled Lesson";

  const fileType = useMemo(() => {
    return FileService.getFileType(_.get(asset, "metadata", ""));
  }, [asset]);

  const readTime = useMemo(() => {
    const videoDuration = _.get(asset, "metadata.video_duration", 0);
    const readDuration = _.get(asset, "metadata.read_time", 0);

    const duration = videoDuration || readDuration;
    if (duration === 0) {
      return null;
    }

    return FileService.formatDuration(videoDuration || readDuration);
  }, [asset]);

  const errors = useSelector((state: any) => {
    let errorList = _.get(state.courses, 'edit.errors') || [];
    errorList = _.filter(errorList, { type: 'lesson', uuid: lesson.uuid });
    return _.size(errorList);
  }, _.isEqual);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: lesson!.uuid || 'new-lesson',
    disabled: readonly,
    data: {
      entity: lesson,
      type: 'lesson'
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleRemove = (e) => {
    e.preventDefault();
    if (onRemove) {
      onRemove(index);
    }
  }

  const handleClick = () => {
    onClick(index);
  }

  return (
    <div style={style} className={cx(styles.entity, styles.lesson, className, {
      [styles.readonly]: readonly,
      [styles.errors]: errors > 0,
      [styles.active]: active
    })} ref={setNodeRef} {...listeners} {...attributes} onClick={handleClick}>
      {!readonly &&
        <i className={cx(styles.dragHandle, "paperflite-drag")}></i>
      }
      <div className={styles.index}>
        {lessonIndex + 1}
      </div>
      <div className={styles.entityDetail}>
        <Thumbnail className={styles.icon} media={_.get(asset, "icon")} defaultImage="/images/assets/default_asset.jpg" />
        <div className={styles.details}>
          <Text className={styles.name} text={name} lines={1} />
          <div className={styles.metadata}>
            <span className={styles.value}>{fileType}</span>
            {readTime &&
              <>
                <span className={styles.separator}>&nbsp;</span>
                <span className={styles.value}>{readTime}</span>
              </>
            }
          </div>
        </div>
      </div>
      {!readonly &&
        <div className={styles.actions}>
          <button className={styles.action} onClick={handleRemove}>
            <i className={'paperflite-delete'}></i>
          </button>
        </div>
      }
      {errors > 0 &&
        <div className={styles.errorIndicator}>
          <span>{errors}</span>
          <i className="fa-solid fa-triangle-exclamation"></i>
        </div>
      }
    </div>
  );
}

export interface AssessmentCardProps {
  assessment: Assessment;
  index: number;
  className?: string;
  active: boolean;
  readonly?: boolean;
  // eslint-disable-next-line no-unused-vars
  onClick: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  onRemove?: (index: number) => void;
}

export const AssessmentCard: React.FC<AssessmentCardProps> = ({ assessment, index, className, active, readonly, onClick, onRemove }) => {
  const navigate = useNavigate();

  const errors = useSelector((state: any) => {
    let errorList = _.get(state.courses, 'edit.errors') || [];
    errorList = _.filter(errorList, { type: 'assessment', uuid: assessment.uuid });
    return _.size(errorList);
  }, _.isEqual);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: assessment!.uuid || 'new-assessment',
    disabled: readonly,
    data: {
      entity: assessment,
      type: 'assessment'
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleRemove = (e) => {
    e.preventDefault();
    if (onRemove) {
      onRemove(index);
    }
  }

  const handleClick = () => {
    onClick(index);
  }

  return (
    <div className={cx(styles.entity, styles.assessment, className, {
      [styles.readonly]: readonly,
      [styles.errors]: errors > 0,
      [styles.active]: active
    })} style={style} ref={setNodeRef} {...listeners} {...attributes} onClick={handleClick}>
      {!readonly &&
        <i className={cx(styles.dragHandle, "paperflite-drag")}></i>
      }
      <div className={styles.index}>A</div>
      <div className={styles.entityDetail}>
        <div className={styles.details}>
          <Text className={styles.name} text={_.get(assessment, 'name') || `Untitled Assessment`} lines={1} />
          <div className={styles.metadata}>
            <span className={styles.value}>{_.size(_.get(assessment, 'questions'))} Questions</span>
          </div>
        </div>
      </div>
      {!readonly &&
        <div className={styles.actions}>
          <button className={styles.action} onClick={handleRemove}>
            <i className={'paperflite-delete'}></i>
          </button>
        </div>
      }
      {errors > 0 &&
        <div className={styles.errorIndicator}>
          <span>{errors}</span>
          <i className="fa-solid fa-triangle-exclamation"></i>
        </div>
      }
    </div>
  );
}

interface PlaceholderCardProps {
  index: number;
}

export const PlaceholderCard: React.FC<PlaceholderCardProps> = ({ index }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)}>
      <div className={styles.index}>{index + 1}</div>
      <div className={styles.entityDetail}>
        <div className={cx(styles.icon, styles.block)}></div>
      </div>
    </div>
  );
}